import { useDocumentCategories } from "@/hooks/use-document-categories";
import type { Id } from "@/packages/types";
import * as Popover from "@radix-ui/react-popover";
import { FolderOpen, Layers } from "lucide-react";
import { useState } from "react";
import { getCategoryLucideIcon } from "../category-icon-map";
import { useDocumentCategoryLabel } from "../use-document-category-label";

interface BatchCategoryAssignProps {
  onAssign: (categoryId: Id<"documentCategories">) => void;
}

/**
 * Compact "Set all" category picker for batch-assigning
 * the same category to all files at once.
 */
export function BatchCategoryAssign({ onAssign }: BatchCategoryAssignProps) {
  const [open, setOpen] = useState(false);
  const { categories, isLoading } = useDocumentCategories();
  const getCategoryLabel = useDocumentCategoryLabel();

  const handleSelect = (categoryId: Id<"documentCategories">) => {
    onAssign(categoryId);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted/50 hover:text-accent transition-colors cursor-pointer"
        >
          <Layers size={11} />
          Set all
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          className="w-[240px] p-0 bg-card border border-border rounded-xl shadow-lg z-[9999] animate-in fade-in-0 zoom-in-95"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="px-3 py-2 border-b border-border/40">
            <span className="text-[11px] font-semibold text-muted/60 uppercase tracking-[0.06em]">
              Apply to all files
            </span>
          </div>
          <div className="max-h-[240px] overflow-y-auto overscroll-contain p-1">
            {isLoading ? (
              <div className="py-4 text-center text-[12px] text-muted/50">
                Loading...
              </div>
            ) : (
              categories.map((category) => {
                const IconComponent = getCategoryLucideIcon(category.icon);
                return (
                  <button
                    key={category._id}
                    type="button"
                    onClick={() => handleSelect(category._id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-secondary hover:text-primary hover:bg-page/60 rounded-lg transition-colors cursor-pointer text-left"
                  >
                    {IconComponent ? (
                      <IconComponent className="h-3.5 w-3.5 shrink-0 text-muted/50" />
                    ) : (
                      <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted/50" />
                    )}
                    <span className="flex-1 truncate">
                      {getCategoryLabel({
                        name: category.name,
                        slug: category.slug,
                      })}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
